import { useState } from 'react'
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { useForbiddenGuard, useWriteForbiddenGuard } from '../api/guard'
import { SEED_MANAGED, writeErrorMessage } from '../api/problems'
import { useIsSuperAdmin } from '../api/tier'
import { ConfirmDialog } from '../components/ConfirmDialog'
import './screens.css'

type SkillStatus = 'draft' | 'active' | 'archived'

interface PlatformSkill {
  id: string
  slug: string
  scope: string | null
  status: SkillStatus
  source: string | null
  version: number | null
  description: string | null
  tool_refs: string[]
  toolkit_refs: string[]
  updated_at: string | null
  created_at: string | null
}
interface SkillsResponse {
  skills: PlatformSkill[]
  has_more: boolean
}

// The S1 transitions the backend allows (anything else is a 409).
const TRANSITIONS: Record<SkillStatus, { to: SkillStatus; label: string }[]> = {
  draft: [
    { to: 'active', label: 'Activate' },
    { to: 'archived', label: 'Archive' },
  ],
  active: [{ to: 'archived', label: 'Archive' }],
  archived: [{ to: 'active', label: 'Reactivate' }],
}

const PAGE_SIZE = 50

type Write =
  | { kind: 'create'; skill_md: string }
  | { kind: 'version'; skill: PlatformSkill; skill_md: string }
  | { kind: 'status'; skill: PlatformSkill; status: SkillStatus }

export function PlatformSkillsView() {
  const qc = useQueryClient()
  const { isSuperAdmin } = useIsSuperAdmin()
  const [status, setStatus] = useState<'' | SkillStatus>('')
  const [offset, setOffset] = useState(0)
  const [createMd, setCreateMd] = useState('')
  const [versionFor, setVersionFor] = useState<PlatformSkill | null>(null)
  const [versionMd, setVersionMd] = useState('')
  const [pending, setPending] = useState<Write | null>(null)
  const [okMsg, setOkMsg] = useState<string | null>(null)

  const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) })
  if (status) params.set('status', status)

  const query = useQuery<SkillsResponse>({
    queryKey: ['admin-skills', status, offset],
    queryFn: () => apiFetch<SkillsResponse>(`/admin/skills?${params.toString()}`),
    placeholderData: keepPreviousData,
  })
  useForbiddenGuard(query.error)

  const write = useMutation({
    mutationFn: (w: Write) => {
      if (w.kind === 'create') {
        return apiFetch<PlatformSkill>('/admin/skills', {
          method: 'POST',
          body: JSON.stringify({ skill_md: w.skill_md }),
        })
      }
      if (w.kind === 'version') {
        return apiFetch<PlatformSkill | { unchanged: true }>(
          `/admin/skills/${encodeURIComponent(w.skill.id)}/versions`,
          {
            method: 'POST',
            body: JSON.stringify({ expected_version: w.skill.version, skill_md: w.skill_md }),
          },
        )
      }
      return apiFetch<PlatformSkill>(`/admin/skills/${encodeURIComponent(w.skill.id)}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: w.status }),
      })
    },
    onSuccess: (data, w) => {
      if (w.kind === 'create') {
        setCreateMd('')
        setOkMsg(`Created ${(data as PlatformSkill)?.slug ?? 'skill'} as draft.`)
      } else if (w.kind === 'version') {
        setVersionFor(null)
        setVersionMd('')
        setOkMsg(
          (data as { unchanged?: boolean })?.unchanged
            ? `${w.skill.slug}: content matches the current version; nothing changed.`
            : `${w.skill.slug}: new version saved.`,
        )
      } else {
        setOkMsg(`${w.skill.slug} is now ${w.status}.`)
      }
      qc.invalidateQueries({ queryKey: ['admin-skills'] })
    },
    onError: () => setOkMsg(null),
  })
  useWriteForbiddenGuard(write.error)

  const skills = query.data?.skills ?? []
  const hasMore = query.data?.has_more ?? false

  return (
    <section className="scr" aria-labelledby="skills-heading">
      <div className="screen-head">
        <h2 id="skills-heading">Platform Skills</h2>
        <select
          className="scr-input scr-input--narrow"
          aria-label="Filter by status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as '' | SkillStatus)
            setOffset(0)
          }}
        >
          <option value="">All statuses</option>
          <option value="draft">draft</option>
          <option value="active">active</option>
          <option value="archived">archived</option>
        </select>
      </div>

      {!isSuperAdmin && (
        <p className="scr-note">
          Read-only for your tier. Creating, versioning and changing the status of platform
          skills require a super admin.
        </p>
      )}

      {query.isError && (query.error as { status?: number }).status !== 403 && (
        <p className="screen-error" role="alert">
          Failed to load platform skills.
        </p>
      )}
      {write.isError && (
        <p className="screen-error" role="alert">
          {writeErrorMessage(write.error)}
        </p>
      )}
      {okMsg && <p className="scr-ok">{okMsg}</p>}

      <table className="scr-table">
        <thead>
          <tr>
            <th>Slug</th>
            <th>Status</th>
            <th>Source</th>
            <th>Version</th>
            <th>Tools</th>
            <th>Updated</th>
            {isSuperAdmin && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {skills.map((s) => {
            const seed = s.source === 'seed'
            return (
              <tr key={s.id} className={s.status === 'archived' ? 'scr-row--muted' : undefined}>
                <td>
                  <span className="scr-mono">{s.slug}</span>
                  {s.description && <div className="scr-muted">{s.description}</div>}
                </td>
                <td>
                  <span className="scr-badge">{s.status}</span>
                </td>
                <td>{s.source ?? '—'}</td>
                <td>{s.version ?? '—'}</td>
                <td>
                  {s.tool_refs.length === 0 ? (
                    <span className="scr-muted">none</span>
                  ) : (
                    <span className="scr-chips">
                      {s.tool_refs.map((t) => (
                        <span key={t} className="scr-chip">
                          {t}
                        </span>
                      ))}
                    </span>
                  )}
                </td>
                <td>{s.updated_at ? new Date(s.updated_at).toLocaleDateString() : '—'}</td>
                {isSuperAdmin && (
                  <td>
                    {seed ? (
                      <span className="scr-muted">{SEED_MANAGED}</span>
                    ) : (
                      <span className="scr-actions">
                        <button
                          type="button"
                          className="screen-btn"
                          aria-label={`New version of ${s.slug}`}
                          disabled={write.isPending}
                          onClick={() => {
                            setVersionMd('')
                            setVersionFor(s)
                          }}
                        >
                          New version
                        </button>
                        {TRANSITIONS[s.status]?.map((t) => (
                          <button
                            key={t.to}
                            type="button"
                            className="screen-btn"
                            aria-label={`${t.label} ${s.slug}`}
                            disabled={write.isPending}
                            onClick={() => setPending({ kind: 'status', skill: s, status: t.to })}
                          >
                            {t.label}
                          </button>
                        ))}
                      </span>
                    )}
                  </td>
                )}
              </tr>
            )
          })}
          {!query.isLoading && skills.length === 0 && (
            <tr>
              <td colSpan={isSuperAdmin ? 7 : 6} className="ws-empty">
                No platform skills.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="ws-pager">
        <button
          type="button"
          disabled={offset === 0}
          onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
        >
          Previous
        </button>
        <span className="ws-count">Page {Math.floor(offset / PAGE_SIZE) + 1}</span>
        <button type="button" disabled={!hasMore} onClick={() => setOffset((o) => o + PAGE_SIZE)}>
          Next
        </button>
      </div>

      {isSuperAdmin && versionFor && (
        <div className="scr-panel">
          <h3>
            New version of <span className="scr-mono">{versionFor.slug}</span> (current v
            {versionFor.version ?? '?'})
          </h3>
          <p className="scr-note">
            Paste the full SKILL.md. Its <code>name</code> must equal the slug; tool refs must be
            listed in <code>metadata.symone-tools</code> and be built-in connector tools.
          </p>
          <textarea
            className="scr-textarea"
            aria-label="New version SKILL.md"
            rows={10}
            value={versionMd}
            onChange={(e) => setVersionMd(e.target.value)}
          />
          <div className="scr-panel-actions">
            <button
              type="button"
              className="screen-btn"
              disabled={!versionMd.trim() || write.isPending}
              onClick={() =>
                setPending({ kind: 'version', skill: versionFor, skill_md: versionMd })
              }
            >
              Save version
            </button>
            <button type="button" className="screen-btn" onClick={() => setVersionFor(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {isSuperAdmin && (
        <div className="scr-panel">
          <h3>Create a platform skill</h3>
          <p className="scr-note">
            Paste a SKILL.md. It is created as <code>draft</code>. Tool refs must be explicit (
            <code>metadata.symone-tools</code>) and built-in connector tools only. Seed skills are
            managed by the seed file and change only through a reviewed PR.
          </p>
          <textarea
            className="scr-textarea"
            aria-label="New skill SKILL.md"
            rows={10}
            value={createMd}
            onChange={(e) => setCreateMd(e.target.value)}
            placeholder={'---\nname: my-skill\ndescription: ...\nmetadata:\n  symone-tools: [...]\n---\n\nBody…'}
          />
          <div className="scr-panel-actions">
            <button
              type="button"
              className="screen-btn"
              disabled={!createMd.trim() || write.isPending}
              onClick={() => setPending({ kind: 'create', skill_md: createMd })}
            >
              Create skill
            </button>
          </div>
        </div>
      )}

      {pending && (
        <ConfirmDialog
          label="Confirm skill change"
          onCancel={() => setPending(null)}
          onConfirm={() => {
            setOkMsg(null)
            write.mutate(pending)
            setPending(null)
          }}
        >
          {pending.kind === 'create' && <p>Create a new platform skill (draft) from this SKILL.md?</p>}
          {pending.kind === 'version' && (
            <p>
              Save a new version of <strong>{pending.skill.slug}</strong> on top of v
              {pending.skill.version ?? '?'}?
            </p>
          )}
          {pending.kind === 'status' && (
            <p>
              Move <strong>{pending.skill.slug}</strong> from {pending.skill.status} to{' '}
              <strong>{pending.status}</strong>?
            </p>
          )}
        </ConfirmDialog>
      )}
    </section>
  )
}
